import { useEffect, useMemo, useRef, useState } from "react";
import { fetchAdminClinics, updateAdminClinic, runSchedulingSetup } from "@services/clinicUsers";
import {
  fetchSikkaAuthorizedPractices,
  matchClinicToSikka,
} from "@services/sikka";
import { filterClinics } from "@utils/clinicSearch";
import { diffClinicPayload, pickEditableClinic } from "@utils/clinicDiff";
import { sanitizeFormValues } from "@utils/sanitize";

import {
  fetchAgentPurposes,
  fetchAgentQuestions,
  fetchClinicAgentQuestionsAll,
  saveClinicAgentQuestionsAll,
} from "@services/agentQuestions";

// Extract purpose values from [{value, is_optional}, ...] format
function getPurposeValues(purposes) {
  if (!purposes?.length) return [];
  // Handle both old format (strings) and new format (objects)
  return purposes.map((p) => (typeof p === "string" ? p : p.value));
}

// Get default purposes (non-optional)
function getDefaultPurposes(purposes) {
  if (!purposes?.length) return [];
  return purposes
    .filter((p) => typeof p === "string" || !p.is_optional)
    .map((p) => (typeof p === "string" ? p : p.value));
}

// Get optional purposes
function getOptionalPurposes(purposes) {
  if (!purposes?.length) return [];
  return purposes
    .filter((p) => typeof p === "object" && p.is_optional)
    .map((p) => p.value);
}

export function ensureByPurposeShape(purposes, input) {
  const base = {};
  const defaultPurposes = getDefaultPurposes(purposes);
  const optionalPurposes = getOptionalPurposes(purposes);

  // Always create keys for default purposes
  for (const p of defaultPurposes) base[p] = [];

  if (!input || typeof input !== "object") return base;

  // Process default purposes
  for (const p of defaultPurposes) {
    const arr = Array.isArray(input[p]) ? input[p] : [];
    base[p] = arr
      .filter((x) => x?.agent_question_enum)
      .map((x, idx) => ({
        agent_question_enum: String(x.agent_question_enum),
        order: Number.isFinite(Number(x.order)) ? Number(x.order) : idx + 1,
        new_patients_only: !!x.new_patients_only,
      }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((x, idx) => ({ ...x, order: idx + 1 }));
  }

  // Only include optional purposes if they have data in input
  for (const p of optionalPurposes) {
    const arr = Array.isArray(input[p]) ? input[p] : null;
    if (arr && arr.length > 0) {
      base[p] = arr
        .filter((x) => x?.agent_question_enum)
        .map((x, idx) => ({
          agent_question_enum: String(x.agent_question_enum),
          order: Number.isFinite(Number(x.order)) ? Number(x.order) : idx + 1,
          new_patients_only: !!x.new_patients_only,
        }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((x, idx) => ({ ...x, order: idx + 1 }));
    }
  }

  return base;
}

export function useAdminClinics() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");

  const [viewModal, setViewModal] = useState({
    open: false,
    title: "",
    text: "",
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedClinicId, setSelectedClinicId] = useState(null);

  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);

  const [saving, setSaving] = useState(false);

  // Agent questions state (new tables)
  const [purposes, setPurposes] = useState([]);
  const [questionLibrary, setQuestionLibrary] = useState([]);
  const [questionLibraryLoading, setQuestionLibraryLoading] = useState(true);

  const [originalAgentQuestionsByPurpose, setOriginalAgentQuestionsByPurpose] =
    useState(null);
  const [agentQuestionsByPurpose, setAgentQuestionsByPurpose] = useState(null);

  const [setupLoading, setSetupLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Sikka integration
  const [sikkaPractices, setSikkaPractices] = useState([]);
  const [sikkaPracticesLoading, setSikkaPracticesLoading] = useState(false);
  const [sikkaConnecting, setSikkaConnecting] = useState(false);

  const initializedFor = useRef({ clinicId: null, modalOpen: false });

  // ------------------------
  // Load clinics
  // ------------------------
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchAdminClinics();
        setClinics(res?.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ------------------------
  // Load purposes + library (one-time)
  // ------------------------
  useEffect(() => {
    (async () => {
      setQuestionLibraryLoading(true);
      try {
        const pRes = await fetchAgentPurposes();
        const qRes = await fetchAgentQuestions();
        // API returns [{value, is_optional}, ...] - store full metadata
        setPurposes(pRes?.data || []);
        setQuestionLibrary(qRes?.data || []);
      } finally {
        setQuestionLibraryLoading(false);
      }
    })();
  }, []);

  const selectedClinic = useMemo(() => {
    if (!selectedClinicId) return null;
    return clinics.find((c) => c.id === selectedClinicId) || null;
  }, [clinics, selectedClinicId]);

  const filteredClinics = useMemo(
    () => filterClinics(clinics, query),
    [clinics, query],
  );

  const openView = (title, text) =>
    setViewModal({ open: true, title, text: text ?? "" });
  const closeView = () => setViewModal({ open: false, title: "", text: "" });

  const openEdit = (clinic) => {
    setSelectedClinicId(clinic.id);
    setEditModalOpen(true);
  };

  const closeEdit = () => {
    setEditModalOpen(false);
    setSelectedClinicId(null);
    setOriginal(null);
    setDraft(null);
    setSaving(false);

    setOriginalAgentQuestionsByPurpose(null);
    setAgentQuestionsByPurpose(null);

    initializedFor.current = { clinicId: null, modalOpen: false };
  };

  // ------------------------
  // Init draft when opening drawer + load clinic agent questions
  // ------------------------
  useEffect(() => {
    if (!editModalOpen || !selectedClinic) return;

    if (
      initializedFor.current.clinicId === selectedClinic.id &&
      initializedFor.current.modalOpen === editModalOpen
    ) {
      return;
    }

    initializedFor.current = {
      clinicId: selectedClinic.id,
      modalOpen: editModalOpen,
    };

    const orig = pickEditableClinic(selectedClinic);
    setOriginal(orig);
    setDraft(orig);

    // Load agent questions from new table (single endpoint)
    (async () => {
      try {
        const res = await fetchClinicAgentQuestionsAll(selectedClinic.id);
        const normalized = ensureByPurposeShape(purposes, res?.data || {});
        setOriginalAgentQuestionsByPurpose(normalized);
        setAgentQuestionsByPurpose(normalized);
      } catch {
        // If none exist yet, start empty
        const empty = ensureByPurposeShape(purposes, {});
        setOriginalAgentQuestionsByPurpose(empty);
        setAgentQuestionsByPurpose(empty);
      }
    })();
  }, [editModalOpen, selectedClinic, purposes]);

  // ------------------------
  // Dirty flags
  // ------------------------
  const dirtyClinic = useMemo(() => {
    if (!original || !draft) return false;
    return Object.keys(diffClinicPayload(original, draft)).length > 0;
  }, [original, draft]);

  const dirtyAgentQuestions = useMemo(() => {
    if (!originalAgentQuestionsByPurpose || !agentQuestionsByPurpose)
      return false;
    return (
      JSON.stringify(originalAgentQuestionsByPurpose) !==
      JSON.stringify(agentQuestionsByPurpose)
    );
  }, [originalAgentQuestionsByPurpose, agentQuestionsByPurpose]);

  // ------------------------
  // draft field setters
  // ------------------------
  const setDraftField = (field, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  };

  // ------------------------
  // Save (clinic patch + clinic agent questions)
  // ------------------------
  const save = async () => {
    if (!selectedClinicId) return;

    setSaving(true);
    try {
      // Save clinic fields if changed
      if (dirtyClinic) {
        const payload = sanitizeFormValues(diffClinicPayload(original, draft));
        const res = await updateAdminClinic(selectedClinicId, payload);
        const updated = res?.data || {};

        setClinics((prev) =>
          prev.map((c) =>
            c.id === selectedClinicId ? { ...c, ...updated } : c,
          ),
        );
      }

      // Save agent questions if changed
      if (dirtyAgentQuestions) {
        await saveClinicAgentQuestionsAll(
          selectedClinicId,
          agentQuestionsByPurpose,
        );
      }

      closeEdit();
    } finally {
      setSaving(false);
    }
  };

  // ------------------------
  // Run scheduling setup
  // ------------------------
  const runSetup = async (clinicId) => {
    const targetId = clinicId || selectedClinicId;
    if (!targetId) return;
    setSetupLoading(true);
    setToast(null);
    try {
      const res = await runSchedulingSetup(targetId);
      const data = res?.data || {};
      const errors = data.errors || [];
      if (errors.length > 0) {
        setToast({ kind: "error", title: "Setup completed with errors", message: errors.join("; ") });
      } else {
        setToast({
          kind: "success",
          title: "Setup complete",
          message: `Providers: ${data.providers_created ?? 0} created, ${data.providers_updated ?? 0} updated. Patients: ${data.patients_created ?? 0} created, ${data.patients_skipped ?? 0} skipped.`,
        });
      }
    } catch (e) {
      const msg = e?.message || e?.details || "Scheduling setup failed";
      setToast({ kind: "error", title: "Setup failed", message: msg });
    } finally {
      setSetupLoading(false);
    }
  };

  // ------------------------
  // Sikka: load authorized offices + connect a clinic
  // ------------------------
  const loadSikkaPractices = async () => {
    setSikkaPracticesLoading(true);
    setToast(null);
    try {
      const res = await fetchSikkaAuthorizedPractices();
      setSikkaPractices(res?.data || []);
    } catch (e) {
      const msg = e?.message || e?.details || "Failed to load Sikka offices";
      setToast({ kind: "error", title: "Could not load Sikka offices", message: msg });
    } finally {
      setSikkaPracticesLoading(false);
    }
  };

  const connectSikka = async (officeId) => {
    if (!selectedClinicId || !officeId) return;
    setSikkaConnecting(true);
    setToast(null);
    try {
      const res = await matchClinicToSikka(selectedClinicId, officeId);
      const data = res?.data || {};
      const errors = data.errors || [];

      // Reflect the new active provider locally so the status pill updates.
      setClinics((prev) =>
        prev.map((c) =>
          c.id === selectedClinicId
            ? { ...c, active_pms_provider: "sikka", pms_config: { ...(c.pms_config || {}), office_id: officeId } }
            : c,
        ),
      );

      if (errors.length > 0) {
        setToast({ kind: "error", title: "Sikka connected with errors", message: errors.join("; ") });
      } else {
        setToast({
          kind: "success",
          title: "Sikka connected",
          message: `Providers: ${data.providers_created ?? 0} created, ${data.providers_updated ?? 0} updated. Patients: ${data.patients_created ?? 0} created, ${data.patients_skipped ?? 0} skipped.`,
        });
      }
    } catch (e) {
      const msg = e?.message || e?.details || "Sikka connection failed";
      setToast({ kind: "error", title: "Sikka connection failed", message: msg });
    } finally {
      setSikkaConnecting(false);
    }
  };

  // If you want: lightweight validation (no duplicates)
  const isValidAgentQuestions = useMemo(() => {
    if (!agentQuestionsByPurpose || !purposes?.length) return true;

    const purposeValues = getPurposeValues(purposes);
    for (const p of purposeValues) {
      const arr = agentQuestionsByPurpose[p];
      if (!Array.isArray(arr)) continue; // Skip purposes that don't exist yet
      const seen = new Set();
      for (const item of arr) {
        if (!item?.agent_question_enum) return false;
        if (seen.has(item.agent_question_enum)) return false;
        seen.add(item.agent_question_enum);
      }
    }
    return true;
  }, [agentQuestionsByPurpose, purposes]);

  return {
    loading,
    clinics,

    query,
    setQuery,
    filteredClinics,

    viewModal,
    openView,
    closeView,

    editModalOpen,
    openEdit,
    closeEdit,

    selectedClinicId,
    selectedClinic,
    draft,
    setDraftField,

    purposes,
    questionLibrary,
    questionLibraryLoading,

    originalAgentQuestionsByPurpose,
    agentQuestionsByPurpose,
    setAgentQuestionsByPurpose,

    dirtyClinic,
    dirtyAgentQuestions,

    saving,
    save,

    isValidAgentQuestions,

    setupLoading,
    runSetup,
    toast,
    setToast,

    sikkaPractices,
    sikkaPracticesLoading,
    sikkaConnecting,
    loadSikkaPractices,
    connectSikka,
  };
}
